import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Table, Form } from 'react-bootstrap';
import api from '../services/api';
import { showToast } from './ToastNotification';
import { sklonFaktur, sklonFakturu } from './invoiceActions';

/**
 * Odeslání faktury e-mailem.
 *
 * Jedna faktura: dialog ukáže adresáta a předmět ještě před odesláním. Adresát
 * je kontaktní osoba z faktury (chodí tam z objednávky), jinak fakturační kontakt
 * zákazníka. Adresu jde před odesláním přepsat — kvůli zákazníkům, kde má každá
 * faktura jít někomu jinému a na objednávce se to nevyplnilo. Když adresát chybí
 * úplně, nabídne se poslat doklad obchodníkovi, aby ho klientovi přeposlal.
 *
 * Víc faktur: odešlou se najednou a vypíše se souhrn; co se nepodařilo
 * (chybí adresát, doklad jde poštou), zůstane v seznamu k vyřízení jednotlivě.
 */
function InvoiceEmailModal({ show, invoices, onHide, onSent }) {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // Ruční adresát — otevře se na odkaz „poslat jinam"
  const [vlastniEmail, setVlastniEmail] = useState('');
  const [vlastniOtevreny, setVlastniOtevreny] = useState(false);

  const list = invoices || [];
  const single = list.length === 1 ? list[0] : null;

  useEffect(() => {
    if (!show) return;
    setResult(null);
    setInfo(null);
    setVlastniEmail('');
    setVlastniOtevreny(false);
    if (!single) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.getInvoiceEmailInfo(single.id);
        if (!cancelled) setInfo(res.data || null);
      } catch (e) {
        if (!cancelled) showToast(e.message || 'Nepodařilo se načíst adresáta.', 'danger');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [show, single]);

  const finish = () => {
    window.dispatchEvent(new CustomEvent('invoices:refresh'));
    if (onSent) onSent();
  };

  const sendSingle = async (toSalesperson) => {
    if (sending) return;
    setSending(true);
    try {
      const rucni = vlastniOtevreny ? vlastniEmail.trim() : '';
      const res = await api.sendInvoiceEmail(single.id, {
        toSalesperson,
        email: rucni || null,
      });
      showToast(`Faktura ${single.number || single.id} odeslaná na ${res.data?.recipient}.`, 'success');
      finish();
      onHide();
    } catch (e) {
      showToast(e.message || 'Fakturu se nepodařilo odeslat.', 'danger');
    } finally {
      setSending(false);
    }
  };

  const sendBulk = async () => {
    if (sending) return;
    setSending(true);
    try {
      const res = await api.sendInvoiceEmails(list.map((i) => i.id));
      setResult(res.data || { sent: [], failed: [] });
      const sent = res.data?.sent?.length || 0;
      showToast(sent > 0 ? `Odesláno ${sent} ${sklonFaktur(sent)}.` : 'Neodeslala se žádná faktura.', sent > 0 ? 'success' : 'warning');
      finish();
    } catch (e) {
      showToast(e.message || 'Odeslání se nepodařilo.', 'danger');
    } finally {
      setSending(false);
    }
  };

  const recipient = info?.recipient;
  const canSendToCustomer = !!recipient?.email;

  return (
    <Modal show={show} onHide={() => !sending && onHide()} centered size={single ? undefined : 'lg'}>
      <Modal.Header closeButton={!sending}>
        <Modal.Title><i className="bi bi-envelope me-2" />Poslat e-mailem</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {single ? (
          loading ? (
            <div className="text-center py-3"><Spinner animation="border" size="sm" className="me-2" />Načítání…</div>
          ) : info ? (
            <>
              <div className="mb-3">
                Odeslat doklad <strong>{info.number || info.id}</strong>
                {info.type_label ? ` (${info.type_label})` : ''} e-mailem?
              </div>
              <Table size="sm" className="align-middle mb-3">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: 110 }}>Předmět</td>
                    <td>{info.subject}</td>
                  </tr>
                  <tr>
                    <td className="text-muted">Adresát</td>
                    <td>
                      {vlastniOtevreny ? (
                        <Form.Control
                          type="email"
                          size="sm"
                          value={vlastniEmail}
                          onChange={(e) => setVlastniEmail(e.target.value)}
                          placeholder="jmeno@domena.cz"
                          autoFocus
                        />
                      ) : canSendToCustomer ? (
                        <>
                          {recipient.name ? `${recipient.name} — ` : ''}<strong>{recipient.email}</strong>
                          {recipient.source === 'invoice_contact' && (
                            /* Kontaktní osoba z dokladu — chodí tam z objednávky, nebo
                               se při vystavení dohledala přes zákazníka. Odtud se to
                               nepozná, proto neutrální popisek. */
                            <span className="text-muted small ms-2">kontakt z dokladu</span>
                          )}
                        </>
                      ) : (
                        <span className="text-danger">nepodařilo se určit</span>
                      )}
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2 align-baseline"
                        onClick={() => { setVlastniOtevreny(!vlastniOtevreny); setVlastniEmail(''); }}
                      >
                        {vlastniOtevreny ? 'zpět na adresáta z faktury' : 'poslat jinam'}
                      </Button>
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Příloha</td>
                    <td>PDF dokladu</td>
                  </tr>
                  {info.sent_date && (
                    <tr>
                      <td className="text-muted">Naposled</td>
                      <td>{info.sent_date}</td>
                    </tr>
                  )}
                </tbody>
              </Table>

              {info.contactWarning && (
                <Alert variant="warning" className="py-2 small">
                  <i className="bi bi-exclamation-triangle me-1" />
                  {info.contactWarning}
                </Alert>
              )}

              {!canSendToCustomer && (
                <Alert variant="warning" className="py-2 small">
                  <i className="bi bi-exclamation-triangle me-1" />
                  {recipient?.reason}
                  {info.salesperson?.email
                    ? <> Můžeš doklad poslat obchodníkovi ({info.salesperson.email}), ať ho klientovi přepošle.</>
                    : <> Obchodník nemá vyplněný e-mail, doklad bude potřeba poslat ručně.</>}
                </Alert>
              )}
            </>
          ) : null
        ) : result ? (
          <>
            <div className="mb-2">
              Odesláno <strong>{result.sent.length}</strong> {sklonFaktur(result.sent.length)}
              {result.failed.length > 0 ? `, neodesláno ${result.failed.length}.` : '.'}
            </div>
            {result.failed.length > 0 && (
              <Table size="sm" className="align-middle">
                <thead className="table-light"><tr><th>Doklad</th><th>Důvod</th></tr></thead>
                <tbody>
                  {result.failed.map((f) => (
                    <tr key={f.id}><td className="text-nowrap">{f.number || f.id}</td><td className="small">{f.error}</td></tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        ) : (
          <>
            <div className="mb-2">
              Odeslat <strong>{list.length}</strong> {sklonFakturu(list.length)} e-mailem?
            </div>
            <div className="small text-muted">
              Každá půjde na kontaktní osobu uvedenou na dokladu (chodí tam z objednávky),
              jinak na kontakt zákazníka s odpovědností „Faktury a upomínky".
              Doklady bez adresáta a doklady posílané poštou se nepošlou a vypíšou se v souhrnu.
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result ? (
          <Button variant="primary" onClick={onHide}>Zavřít</Button>
        ) : single ? (
          <>
            <Button variant="outline-secondary" onClick={onHide} disabled={sending}>Zrušit</Button>
            {!canSendToCustomer && info?.salesperson?.email && (
              <Button variant="outline-primary" onClick={() => sendSingle(true)} disabled={sending}>
                <i className="bi bi-person-badge me-1" />Poslat obchodníkovi
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => sendSingle(false)}
              disabled={sending || loading || (vlastniOtevreny ? !vlastniEmail.trim() : !canSendToCustomer)}
            >
              {sending ? <><Spinner animation="border" size="sm" className="me-1" />Odesílám…</> : <><i className="bi bi-send me-1" />Odeslat</>}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" onClick={onHide} disabled={sending}>Zrušit</Button>
            <Button variant="primary" onClick={sendBulk} disabled={sending}>
              {sending ? <><Spinner animation="border" size="sm" className="me-1" />Odesílám…</> : <><i className="bi bi-send me-1" />Odeslat</>}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default InvoiceEmailModal;
